// The waitlist survey island. Owns step state, an in-flight/error flag, and
// the API lifecycle.
//
// Post-reorder flow:
//   1. "signup"          — parent info + athletes. POST /v1/survey on submit,
//                          fires `Lead` Pixel event.
//   2. "research-prompt" — interstitial: Continue or Skip the optional research.
//   3. "research-bucket" — optional: family bucket + top reasons. PATCH /v1/survey/:id
//   4. "research-prices" — optional: price sentiment, sibling, season. PATCH /v1/survey/:id
//                          fires `ResearchCompleted` Pixel event on submit.
//   5. "thanks"          — final confirmation.
//
// The returned id lives in sessionStorage so a mid-flow refresh doesn't orphan
// the row, and a refreshed-already-signed-up tab lands on the research prompt.
//
// Note on component mapping (legacy filenames preserved):
//   - Step3.tsx → signup (renders first)
//   - Step1.tsx → research bucket (renders second)
//   - Step2.tsx → research prices (renders third)

import { useEffect, useRef, useState } from "react";

import type { AgeBand, City, Gender } from "@fsl/shared";

import { ApiError, surveyApi } from "./api.js";
import { readStoredAttribution } from "./attribution.js";
import { Step1 } from "./Step1.js";
import { Step2 } from "./Step2.js";
import { Step3 } from "./Step3.js";
import { ThankYou } from "./ThankYou.js";
import { initialSurveyState, type Step, type SurveyState } from "./types.js";
import {
  isResearchBucketValid,
  isResearchPricesValid,
  isSignupValid,
} from "./validation.js";

const SESSION_KEY = "fsl:surveyId";
const DONE_KEY = "fsl:surveyDone";

const GENERIC_ERROR =
  "Something went wrong on our end. Please try again, or email team@joinfsl.com if it keeps happening.";
const SESSION_EXPIRED =
  "Your session expired. Please start over — sorry about that.";

const STEP_LABELS: Record<Step, string> = {
  "signup": "Sign up for Fall 2026",
  "research-prompt": "You're in. Help us shape this?",
  "research-bucket": "Optional · About your family",
  "research-prices": "Optional · About price",
  "thanks": "Done. You're in for Fall 2026.",
};

// Ordered for dotClass — dot fills as the user moves through real form steps.
// Interstitial doesn't get its own dot.
const STEP_ORDER: ReadonlyArray<Step> = [
  "signup",
  "research-prompt",
  "research-bucket",
  "research-prices",
  "thanks",
];

function scrollToForm(): void {
  document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function readStoredId(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}
function writeStoredId(id: string): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, id);
  } catch {
    // sessionStorage disabled — we'll still have it in React state for the session
  }
}
function clearStoredId(): void {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

function readStoredDone(): boolean {
  try {
    return window.sessionStorage.getItem(DONE_KEY) === "1";
  } catch {
    return false;
  }
}
function writeStoredDone(): void {
  try {
    window.sessionStorage.setItem(DONE_KEY, "1");
  } catch {
    // ignore
  }
}

export function Survey(): JSX.Element {
  // Mount priority: done flag → thanks; id stored → research-prompt (signed
  // up but didn't finish research); else fresh.
  const storedDone = readStoredDone();
  const storedId = storedDone ? null : readStoredId();
  const [surveyId, setSurveyId] = useState<string | null>(storedId);
  const [step, setStep] = useState<Step>(
    storedDone ? "thanks" : storedId ? "research-prompt" : "signup",
  );
  const [state, setState] = useState<SurveyState>(initialSurveyState);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Funnel tracking. Fire each step's view event once per mount.
  const firedSteps = useRef<Set<Step>>(new Set());
  useEffect(() => {
    if (firedSteps.current.has(step)) return;
    firedSteps.current.add(step);
    const eventName: Record<Step, string | null> = {
      "signup": "SignupView",
      "research-prompt": "ResearchPromptView",
      "research-bucket": "ResearchStep1View",
      "research-prices": "ResearchStep2View",
      "thanks": null,
    };
    const name = eventName[step];
    if (name) window.fbq?.("trackCustom", name);
  }, [step]);

  const patch = (p: Partial<SurveyState>): void => setState((prev) => ({ ...prev, ...p }));

  const go = (next: Step): void => {
    setStep(next);
    scrollToForm();
  };

  const handleApiError = (err: unknown): void => {
    if (err instanceof ApiError && err.status === 404) {
      // Stale id — signed up in a previous session but row no longer exists.
      // Clear local state and restart at signup.
      clearStoredId();
      setSurveyId(null);
      setStep("signup");
      setError(SESSION_EXPIRED);
      return;
    }
    setError(GENERIC_ERROR);
  };

  const onSignupSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (step !== "signup" || submitting) return;
    // Honeypot: bot filled it → silently succeed.
    if (state.website.trim() !== "") {
      writeStoredDone();
      clearStoredId();
      setSurveyId(null);
      go("thanks");
      return;
    }
    if (!isSignupValid(state)) return;

    setError(null);
    setSubmitting(true);
    try {
      const { id } = await surveyApi.signup({
        parentName: state.parentName.trim(),
        parentEmail: state.parentEmail.trim(),
        city: state.city as City,
        consent: true,
        athletes: state.athletes.map((a) => ({
          ageBand: a.ageBand as AgeBand,
          gender: a.gender as Gender,
        })),
        ...readStoredAttribution(),
      });
      writeStoredId(id);
      setSurveyId(id);
      // Meta Pixel conversion — fires on real signup, before any research.
      // Honeypot path above returns before reaching here.
      window.fbq?.("track", "Lead");
      go("research-prompt");
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const onStartResearch = (): void => {
    go("research-bucket");
  };

  const onSkipResearch = (): void => {
    writeStoredDone();
    clearStoredId();
    setSurveyId(null);
    go("thanks");
  };

  const onResearchBucketNext = async (): Promise<void> => {
    if (submitting || !isResearchBucketValid(state)) return;
    if (!surveyId) {
      // Shouldn't happen — no id but in research. Send to thanks.
      writeStoredDone();
      go("thanks");
      return;
    }
    if (!state.bucket) return;
    setError(null);
    setSubmitting(true);
    try {
      await surveyApi.update(surveyId, {
        currentBucket: state.bucket,
        topReasons: state.reasons,
      });
      go("research-prices");
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const onResearchPricesSubmit = async (): Promise<void> => {
    if (submitting || !isResearchPricesValid(state)) return;
    if (!surveyId) {
      writeStoredDone();
      go("thanks");
      return;
    }
    if (!state.priceFsl || !state.priceCategory || !state.sibling || !state.season) return;
    setError(null);
    setSubmitting(true);
    try {
      await surveyApi.update(surveyId, {
        priceSentimentFsl: state.priceFsl,
        priceSentimentCategory: state.priceCategory,
        siblingDiscountImpact: state.sibling,
        seasonLengthPref: state.season,
      });
      window.fbq?.("trackCustom", "ResearchCompleted");
      writeStoredDone();
      clearStoredId();
      setSurveyId(null);
      go("thanks");
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="progress">
        <div className={"progress-dot" + dotClass(step, "signup")} data-step="1"></div>
        <div className={"progress-dot" + dotClass(step, "research-bucket")} data-step="2"></div>
        <div className={"progress-dot" + dotClass(step, "research-prices")} data-step="3"></div>
        <span className="progress-label">{STEP_LABELS[step]}</span>
      </div>

      <form
        id="waitlist-form"
        className="form-card"
        autoComplete="off"
        noValidate
        onSubmit={onSignupSubmit}
      >
        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}

        {/* honeypot */}
        <input
          type="text"
          className="honeypot"
          name="website"
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="off"
          value={state.website}
          onChange={(e) => patch({ website: e.target.value })}
        />

        {step === "signup" && (
          <Step3
            state={state}
            patch={patch}
            canSubmit={isSignupValid(state) && !submitting}
            submitting={submitting}
          />
        )}

        {step === "research-prompt" && (
          <div className="step active research-prompt" data-step="research-prompt">
            <h3>You're in for Fall 2026.</h3>
            <p>
              Want to help us shape what we build? Two minutes of optional questions
              about your family and what matters to you.
            </p>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={onSkipResearch}>
                Not right now
              </button>
              <div className="right-side">
                <button type="button" className="btn btn-primary" onClick={onStartResearch}>
                  Sure, ask away →
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "research-bucket" && (
          <Step1
            state={state}
            patch={patch}
            onNext={onResearchBucketNext}
            canAdvance={isResearchBucketValid(state) && !submitting}
          />
        )}

        {step === "research-prices" && (
          <Step2
            state={state}
            patch={patch}
            onBack={() => go("research-bucket")}
            onNext={onResearchPricesSubmit}
            canAdvance={isResearchPricesValid(state) && !submitting}
          />
        )}

        {step === "thanks" && <ThankYou />}
      </form>
    </>
  );
}

function dotClass(current: Step, dot: Step): string {
  const ci = STEP_ORDER.indexOf(current);
  const di = STEP_ORDER.indexOf(dot);
  if (ci === di) return " active";
  if (current === "thanks" || ci > di) return " done";
  return "";
}
